import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, notification, type TableColumnsType } from "antd";
import axios from "axios";
import moment from "moment";

export interface DataTypeCSTA {
    key: string;
    ship_to_name: string;
    address: string;
    lead_times: string;
    contact_person: string;
    status: string;
    rt_rw?: string;
    provinsi?: string;
    kabupaten?: string;
    kecamatan?: string;
    kelurahan?: string;
    kode_pos?: string;
    phone_number?: string;
    distribution_route?: string;
    job_title?: string;
}

export const columns: TableColumnsType<DataTypeCSTA> = [
    {
        title: "Ship To Name",
        dataIndex: "ship_to_name",
        key: "ship_to_name",
    },
    {
        title: "Address",
        dataIndex: "address",
        key: "address",
    },
    {
        title: "Lead Times",
        dataIndex: "lead_times",
        key: "lead_times",
    },
    {
        title: "Contact Person",
        dataIndex: "contact_person",
        key: "contact_person",
    },
    {
        title: "Status",
        dataIndex: "status",
        key: "status",
    },
    {
        title: "RT/RW",
        dataIndex: "rt_rw",
        key: "rt_rw",
    },
    {
        title: "Provinsi",
        dataIndex: "provinsi",
        key: "provinsi",
    },
    {
        title: "Kabupaten",
        dataIndex: "kabupaten",
        key: "kabupaten",
    },
    {
        title: "Kecamatan",
        dataIndex: "kecamatan",
        key: "kecamatan",
    },
    {
        title: "Kelurahan",
        dataIndex: "kelurahan",
        key: "kelurahan",
    },
    {
        title: "Kode Pos",
        dataIndex: "kode_pos",
        key: "kode_pos",
    },
    {
        title: "Phone Number",
        dataIndex: "phone_number",
        key: "phone_number",
    },
    {
        title: "Distribution Route",
        dataIndex: "distribution_route",
        key: "distribution_route",
    },

    {
        title: "Job Title",
        dataIndex: "job_title",
        key: "job_title",
    },
    {
        title: "Action",
        key: "action",
    },
];

export interface DataTypeCOP {
    key: React.Key;
    contact_code: string;
    contact_name: string;
    description: string;
    telephone: string;
    status: string;
}

export const columnsCOP: TableColumnsType<DataTypeCOP> = [
    {
        title: "Contact Code",
        dataIndex: "contact_code",
        key: "contact_code",
    },
    {
        title: "Contact Name",
        dataIndex: "contact_name",
        key: "contact_name",
    },
    {
        title: "Description",
        dataIndex: "description",
        key: "description",
    },
    {
        title: "Phone",
        dataIndex: "telephone",
        key: "telephone",
    },
    {
        title: "Status",
        dataIndex: "status",
        key: "status",
    },
    {
        title: "Action",
        key: "action",
    },
];

export interface DataTypeBank {
    key: React.Key;
    status: string;
    bank_code: string;
    account_no: string;
    account_name: string;
    account_type: string;
    currency: string;
}

export const columnsBank: TableColumnsType<DataTypeBank> = [
    {
        title: "Bank Name",
        dataIndex: "bank_code",
        key: "bank_code",
    },
    {
        title: "Account Type",
        dataIndex: "account_type",
        key: "account_type",
    },
    {
        title: "Currency",
        dataIndex: "currency",
        key: "currency",
    },
    {
        title: "Account No",
        dataIndex: "account_no",
        key: "account_no",
    },
    {
        title: "Name",
        dataIndex: "account_name",
        key: "account_name",
    },
    {
        title: "Status",
        dataIndex: "status",
        key: "status",
    },
    {
        title: "Action",
        key: "action",
    },
];

export interface CustomerType {
    code: string;
    deskripsi: string;
}

export interface IdType {
    code: string;
    deskripsi: string;
}

export interface ProvinsiType {
    kode_provinsi: string;
    nama_provinsi: string;
}

export interface KabupatenType {
    kode_dati: string;
    deskripsi_kode_dati: string;
}

export interface KecamatanType {
    kode_kecamatan: string;
    deskripsi_kode_kecamatan: string;
}

export interface KelurahanType {
    kode_kelurahan: string;
    deskripsi_kode_kelurahan: string;
    kode_pos: string;
}

export interface TermOfPaymentType {
    code: string;
    deskripsi: string;
}

export interface TaxInvoiceType {
    code: string;
    deskripsi: string;
}

export interface VatTransactionType {
    code: string;
    deskripsi: string;
}

export interface BusinessType {
    code: string;
    deskripsi: string;
}

export interface CustomerBehaviourType {
    code: string;
    deskripsi: string;
}

export interface JobTitleType {
    code: string;
    deskripsi: string;
}

export interface WarehouseType {
    warehouse_code: string;
    warehouse_name: string;
}


const backendUrl = process.env.REACT_APP_BACKEND_URL;

const useCustomerMasterLogic = () => {
    const [form] = Form.useForm();
    const isInitialLoad = useRef(true);
    const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
    const [idTypes, setIdTypes] = useState<IdType[]>([]);
    const [showIdSection, setShowIdSection] = useState<boolean>(true);
    const [sameAsIdAddressChecked, setSameAsIdAddressChecked] = useState<boolean>(false);
    const [provinces, setProvinces] = useState<ProvinsiType[]>([]);
    const [kabupatens, setKabupatens] = useState<KabupatenType[]>([]);
    const [kecamatans, setKecamatans] = useState<KecamatanType[]>([]);
    const [kelurahans, setKelurahans] = useState<KelurahanType[]>([]);
    const [termOfPayments, setTermOfPayments] = useState<TermOfPaymentType[]>([]);
    const [taxInvoiceTypes, setTaxInvoiceTypes] = useState<TaxInvoiceType[]>([]);
    const [vatTransactionTypes, setVatTransactionTypes] = useState<VatTransactionType[]>([]);
    const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
    const [customerBehaviours, setCustomerBehaviours] = useState<CustomerBehaviourType[]>([]);
    const [jobTitles, setJobTitles] = useState<JobTitleType[]>([]);
    const [dealerNames, setDealerNames] = useState<WarehouseType[]>([]);
    const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('stopped');
    const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const requiredFieldsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isKppModalVisible, setIsKppModalVisible] = useState(false);
    const [prefilledCustomerData, setPrefilledCustomerData] = useState<any | null>(null);
    const [isActiveButtonDisabled, setIsActiveButtonDisabled] = useState(initialRecordStatus === 'Active');
    const [isDeactivateButtonDisabled, setIsDeactivateButtonDisabled] = useState(false);
    const [isCustomerTypeDisabled, setIsCustomerTypeDisabled] = useState<boolean>(false);
    const [existingCustomers, setExistingCustomers] = useState<any[]>([]);
    const [similarNames, setSimilarNames] = useState<string[]>([]);

    const handleNew = useCallback(() => {
        form.resetFields();
        setAutosaveStatus('stopped');
        setIsActiveButtonDisabled(initialRecordStatus === 'Active' || initialRecordStatus === '1');
        setIsDeactivateButtonDisabled(false);
        setIsCustomerTypeDisabled(false);
        notification.destroy('requiredFieldsNotification');
        notification.destroy('similarNamesNotification');
        notification.success({
            message: "Reset form success",
        });
    }, [form]);

    const showKppModal = () => {
        setIsKppModalVisible(true);
    };
    const handleKppModalOk = () => {
        setIsKppModalVisible(false);

    };

    const handleKppModalCancel = () => {
        setIsKppModalVisible(false);
    };


    const saveFormData = useCallback(async (values: any) => {
        const customerType = form.getFieldValue('customer_type');
        const customerCode = form.getFieldValue('cs_code');
        const recordStatus = values.record_status;

        if (!customerType || !customerCode || (recordStatus !== 'Active' && recordStatus !== '1' && recordStatus !== 'Deactive' && recordStatus !== '2')) {
            setAutosaveStatus('stopped');
            console.log("Autosave dihentikan: Customer Type, Customer Code, atau Record Status tidak valid untuk autosave.");
            return;
        }

        try {
            const dataToSave = { ...values };
            if (dataToSave.record_status === "Active") {
                dataToSave.record_status = "1";
            } else if (dataToSave.record_status === "Deactive") {
                dataToSave.record_status = "2";
            }
            const dateFields = [
                'vatnpwpdate', 'vatpkpdate', 'birthday', 'marriagedate', 'contactdate'
            ];
            dateFields.forEach(field => {
                if (dataToSave[field] && moment.isMoment(dataToSave[field])) {
                    dataToSave[field] = dataToSave[field].format('YYYY-MM-DD');
                } else if (dataToSave[field] === null || dataToSave[field] === undefined) {
                    dataToSave[field] = null;
                }
            });

            await axios.post(`${backendUrl}/api/customer-master/autosave`, dataToSave);
            setAutosaveStatus('idle');
            console.log("Autosave berhasil!");
        } catch (error) {
            setAutosaveStatus('error');
            console.error("Autosave gagal:", error);
            notification.error({ message: "Autosave gagal. Periksa konsol untuk detail." });
        }
    }, [form]);

    const checkRequiredFieldsAndNotify = useCallback((allValues: any) => {
        if (requiredFieldsTimeoutRef.current) {
            clearTimeout(requiredFieldsTimeoutRef.current);
        }
        requiredFieldsTimeoutRef.current = setTimeout(() => {
            const missingFields: string[] = [];
            const isFieldEmpty = (fieldName: string) => {
                let val = allValues ? allValues[fieldName] : undefined;
                if (val === undefined || val === null || val === '') {
                    val = form.getFieldValue(fieldName);
                }
                if (val === undefined || val === null || val === '') {
                    const el = (document.getElementById(fieldName) || document.getElementsByName(fieldName)[0]) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | undefined;
                    val = el ? el.value : undefined;
                }
                return val === undefined || val === null || val === '';
            };
            if (isFieldEmpty("customer_type")) {
                missingFields.push("Customer Type");
            }
            if (isFieldEmpty("id_type")) {
                missingFields.push("ID Type");
            }
            if (isFieldEmpty("id_no")) {
                missingFields.push("ID No");
            }
            if (isFieldEmpty("cs_name")) {
                missingFields.push("Customer Name");
            }
            if (isFieldEmpty("address_line1")) {
                missingFields.push("ID Address");
            }
            if (isFieldEmpty("home_address_line1")) {
                missingFields.push("Home Address");
            }
            if (isFieldEmpty("office_address_line1")) {
                missingFields.push("Office Address");
            }
            if (isFieldEmpty("vat_address1")) {
                missingFields.push("VAT Address");
            }
            if (missingFields.length > 0) {
                notification.warning({
                    key: 'requiredFieldsNotification',
                    message: 'Harap lengkapi data form dibawah ini',
                    description: (
                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            {missingFields.map((field) => (
                                <li key={field}>{field}</li>
                            ))}
                        </ul>
                    ),
                    duration: 0,
                    placement: 'topRight',
                });
            } else {
                notification.destroy('requiredFieldsNotification');
            }
        }, 100);
    }, [form]);

    useEffect(() => {
        const allValues = form.getFieldsValue();
        const customerType = allValues.customer_type;
        const customerCode = allValues.cs_code;
        const recordStatus = allValues.record_status;
        if (customerType && customerCode && (recordStatus === 'Active' || recordStatus === '1')) {
            checkRequiredFieldsAndNotify(allValues);
        } else {
            notification.destroy('requiredFieldsNotification');
        }
    }, [showIdSection, form, checkRequiredFieldsAndNotify]);

    const triggerAutosave = useCallback((allValues: any) => {
        const customerType = allValues.customer_type;
        const customerCode = allValues.cs_code;
        const recordStatus = allValues.record_status;

        setIsActiveButtonDisabled(recordStatus === 'Active' || recordStatus === '1');
        setIsDeactivateButtonDisabled(recordStatus === 'Deactive' || recordStatus === '2');
        setIsCustomerTypeDisabled(recordStatus === 'Deactive' || recordStatus === '2');

        if (customerType && customerCode && (recordStatus === 'Active' || recordStatus === '1')) {
            if (autosaveTimeoutRef.current) {
                clearTimeout(autosaveTimeoutRef.current);
            }
            setAutosaveStatus('processing');
            autosaveTimeoutRef.current = setTimeout(() => {
                saveFormData(allValues);
            }, 200);
            checkRequiredFieldsAndNotify(allValues);
        } else {

            setAutosaveStatus('stopped');
            if (autosaveTimeoutRef.current) {
                clearTimeout(autosaveTimeoutRef.current);
            }
            notification.destroy('requiredFieldsNotification')
            notification.destroy('similarNamesNotification');
        }
    }, [saveFormData, setAutosaveStatus, checkRequiredFieldsAndNotify]);

    const onSelectKpp = useCallback((kppCode: string, kppName: string) => {
        form.setFieldsValue({
            vatkppcode: kppCode,
            vatkppdeskripsi: kppName,
        });
        setIsKppModalVisible(false);
        triggerAutosave(form.getFieldsValue());
    }, [form, triggerAutosave]);

    const formatRupiahInput = useCallback((value: number | string | undefined): string => {
        if (value === undefined || value === null || value === '') {
            return '';
        }
        const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) : value;
        if (isNaN(num)) {
            return '';
        }
        return new Intl.NumberFormat('id-ID', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            useGrouping: true,
        }).format(num);
    }, []);

    const parseRupiahInput = useCallback((value: string | undefined): number | undefined => {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }

        const cleanedValue = value.replace(/\./g, '').replace(/,/g, '.');
        const parsed = parseFloat(cleanedValue);
        return isNaN(parsed) ? undefined : parsed;
    }, []);

    const getFormattedCustomerCode = (latestCode: string | null): string => {
        let nextSequence = 1;
        let paddingLength = 1;

        if (latestCode && latestCode.startsWith("10100000")) {
            const existingSequenceStr = latestCode.substring(8);
            const existingSequence = parseInt(existingSequenceStr, 10);
            if (!isNaN(existingSequence)) {
                nextSequence = existingSequence + 1;
                paddingLength = existingSequenceStr.length;
            }
        }

        const formattedSequence = String(nextSequence).padStart(paddingLength, "0");
        return `10100000${formattedSequence}`;
    };

    const fetchLatestCustomerCode = async (): Promise<string | null> => {
        try {
            const response = await axios.get(`${backendUrl}/api/customer-master/latest-code`);
            return response.data.latestCode;
        } catch (error) {
            console.error("Gagal mengambil kode pelanggan terbaru:", error);
            notification.error({ message: "Gagal mengambil kode pelanggan terbaru dari server." });
            return null;
        }
    };

    const generateNewCustomerCode = useCallback(async () => {
        const latestExistingCode = await fetchLatestCustomerCode();
        const newCustomerCode = getFormattedCustomerCode(latestExistingCode);
        form.setFieldsValue({ cs_code: newCustomerCode, record_status: initialRecordStatus });
        setIsActiveButtonDisabled(initialRecordStatus === 'Active' || initialRecordStatus === '1');
        setIsDeactivateButtonDisabled(false);
        setIsCustomerTypeDisabled(false);
        setAutosaveStatus('stopped');
    }, [form]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            const trimmedValue = value.trim();
            if (trimmedValue.length >= 3) {
                const searchVal = trimmedValue.toLowerCase();
                const currentCsCode = form.getFieldValue("cs_code");
                const matches = existingCustomers
                    .filter(c => c.cs_name?.toLowerCase().includes(searchVal) && c.cs_code !== currentCsCode);

                if (matches.length > 0) {
                    const exactMatch = matches.find(c => c.cs_name?.trim().toLowerCase() === searchVal);
                    if (exactMatch) {
                        notification.warning({
                            key: "similarNamesNotification",
                            message: "Customer Sudah Ada",
                            description: `Nama Customer ini sudah ada pada nomor ${exactMatch.cs_code} ini.`,
                        });
                    } else {
                        notification.warning({
                            key: "similarNamesNotification",
                            message: "Customer Serupa Ditemukan",
                            description: (
                                <div>
                                    <p>Nama mirip dengan customer yang sudah ada:</p>
                                    <ul style={{ paddingLeft: "15px", margin: 0 }}>
                                        {matches.slice(0, 5).map(c => (
                                            <li key={c.cs_code}>
                                                <strong>{c.cs_name}</strong> ({c.cs_code})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                            duration: 0,
                        });
                    }
                    setSimilarNames(matches.map(c => `${c.cs_name} (${c.cs_code})`).slice(0, 5));
                } else {
                    notification.destroy("similarNamesNotification");
                    setSimilarNames([]);
                }
            } else {
                notification.destroy("similarNamesNotification");
                setSimilarNames([]);
            }
        }, 1000);
    }, [existingCustomers, form]);

    const handleActivateCustomer = useCallback(async () => {
        const currentCsCode = form.getFieldValue('cs_code');
        if (!currentCsCode) {
            notification.warning({ message: "Customer Code belum ada. Tidak bisa mengaktifkan." });
            return;
        }

        try {
            form.setFieldsValue({ record_status: 'Active' });
            const valuesToSave = { ...form.getFieldsValue(), record_status: '1' };
            await saveFormData(valuesToSave);
            setIsCustomerTypeDisabled(false);
            setAutosaveStatus('idle');
            setIsActiveButtonDisabled(true);
            setIsDeactivateButtonDisabled(false);
            notification.success({ message: "Customer berhasil diaktifkan." });
            triggerAutosave(form.getFieldsValue());
        } catch (error) {
            console.error("Gagal mengaktifkan pelanggan:", error);
            notification.error({ message: "Gagal mengaktifkan pelanggan. Silakan coba lagi." });
        }
    }, [form, saveFormData, triggerAutosave]);

    const handleDeactivateCustomer = useCallback(async () => {
        const currentCsCode = form.getFieldValue('cs_code');
        if (!currentCsCode) {
            notification.warning({ message: "Customer Code belum ada. Tidak bisa menonaktifkan." });
            return;
        }

        try {
            form.setFieldsValue({ record_status: 'Deactive' });
            const valuesToSave = { ...form.getFieldsValue(), record_status: '2' };
            await saveFormData(valuesToSave);
            setIsCustomerTypeDisabled(true);
            setAutosaveStatus('stopped');
            setIsActiveButtonDisabled(false);
            setIsDeactivateButtonDisabled(true);
            notification.destroy('requiredFieldsNotification');
            notification.success({ message: "Customer berhasil dinonaktifkan." });
        } catch (error) {
            console.error("Gagal menonaktifkan pelanggan:", error);
            notification.error({ message: "Gagal menonaktifkan pelanggan. Silakan coba lagi." });
        }
    }, [form, saveFormData]);

    const fetchCustomerTypes = useCallback(async () => {
        try {
            const response = await axios.get<CustomerType[]>(`${backendUrl}/api/customer-master/customer-types`);
            setCustomerTypes(response.data);
        } catch (error) {
            console.error("Gagal mengambil tipe pelanggan:", error);
            notification.error({ message: "Gagal mengambil tipe pelanggan dari server." });
        }
    }, []);

    const fetchIdTypes = useCallback(async () => {
        try {
            const response = await axios.get<IdType[]>(`${backendUrl}/api/customer-master/id-types`);
            setIdTypes(response.data);
        } catch (error) {
            console.error("Gagal mengambil tipe ID:", error);
            notification.error({ message: "Gagal mengambil tipe ID dari server." });
        }
    }, []);

    const fetchProvinces = useCallback(async () => {
        try {
            const response = await axios.get<ProvinsiType[]>(`${backendUrl}/api/customer-master/provinces`);
            setProvinces(response.data);
        } catch (error) {
            console.error("Gagal mengambil data provinsi:", error);
            notification.error({ message: "Gagal mengambil data provinsi dari server." });
        }
    }, []);

    const fetchKabupatens = useCallback(async (provinsiCode: string) => {
        if (!provinsiCode) {
            setKabupatens([]);
            return;
        }
        try {
            const response = await axios.get<KabupatenType[]>(
                `${backendUrl}/api/customer-master/kabupatens/${provinsiCode}`,
            );
            setKabupatens(response.data);
        } catch (error) {
            console.error(`Gagal mengambil data kabupaten untuk provinsi ${provinsiCode}:`, error);
            notification.error({ message: "Gagal mengambil data kabupaten dari server." });
        }
    }, []);

    const fetchKecamatans = useCallback(async (kabupatenCode: string) => {
        if (!kabupatenCode) {
            setKecamatans([]);
            return;
        }
        try {
            const response = await axios.get<KecamatanType[]>(
                `${backendUrl}/api/customer-master/kecamatans/${kabupatenCode}`,
            );
            setKecamatans(response.data);
        } catch (error) {
            console.error(`Gagal mengambil data kecamatan untuk kabupaten ${kabupatenCode}:`, error);
            notification.error({ message: "Gagal mengambil data kecamatan dari server." });
        }
    }, []);

    const fetchKelurahans = useCallback(async (kecamatanCode: string) => {
        if (!kecamatanCode) {
            setKelurahans([]);
            return;
        }
        try {
            const response = await axios.get<KelurahanType[]>(
                `${backendUrl}/api/customer-master/kelurahans/${kecamatanCode}`,
            );
            setKelurahans(response.data);
        } catch (error) {
            console.error(`Gagal mengambil data kelurahan untuk kecamatan ${kecamatanCode}:`, error);
            notification.error({ message: "Gagal mengambil data kelurahan dari server." });
        }
    }, []);

    const fetchTermOfPayments = useCallback(async () => {
        try {
            const response = await axios.get<TermOfPaymentType[]>(`${backendUrl}/api/customer-master/term-of-payments`);
            setTermOfPayments(response.data);
        } catch (error) {
            console.error("Gagal mengambil data Term Of Payment:", error);
            notification.error({ message: "Gagal mengambil data Term Of Payment dari server." });
        }
    }, []);

    const fetchTaxInvoiceTypes = useCallback(async () => {
        try {
            const response = await axios.get<TaxInvoiceType[]>(`${backendUrl}/api/customer-master/tax-invoice-types`);
            setTaxInvoiceTypes(response.data);
        } catch (error) {
            console.error("Gagal mengambil data Tipe Faktur Pajak:", error);
            notification.error({ message: "Gagal mengambil data Tipe Faktur Pajak dari server." });
        }
    }, []);

    const fetchVatTransactionTypes = useCallback(async () => {
        try {
            const response = await axios.get<VatTransactionType[]>(`${backendUrl}/api/customer-master/vat-transaction-types`);
            setVatTransactionTypes(response.data);
        } catch (error) {
            console.error("Gagal mengambil data Tipe Transaksi PPN:", error);
            notification.error({ message: "Gagal mengambil data Tipe Transaksi PPN dari server." });
        }
    }, []);

    const fetchBusinessTypes = useCallback(async () => {
        try {
            const response = await axios.get<BusinessType[]>(`${backendUrl}/api/customer-master/business-types`);
            setBusinessTypes(response.data);
        } catch (error) {
            console.error("Gagal mengambil data Tipe Bisnis:", error);
            notification.error({ message: "Gagal mengambil data Tipe Bisnis dari server." });
        }
    }, []);

    const fetchCustomerBehaviours = useCallback(async () => {
        try {
            const response = await axios.get<CustomerBehaviourType[]>(`${backendUrl}/api/customer-master/customer-behaviours`);
            setCustomerBehaviours(response.data);
        } catch (error) {
            console.error("Gagal mengambil data Customer Behaviour:", error);
            notification.error({ message: "Gagal mengambil data Customer Behaviour dari server." });
        }
    }, []);

    const fetchJobTitles = useCallback(async () => {
        try {
            const response = await axios.get<JobTitleType[]>(`${backendUrl}/api/customer-master/job-titles`);
            setJobTitles(response.data);
        } catch (error) {
            console.error("Gagal mengambil data Job Titles:", error);
            notification.error({ message: "Gagal mengambil data Job Titles dari server." });
        }
    }, []);

    const fetchDealerNames = useCallback(async () => {
        try {
            const response = await axios.get<WarehouseType[]>(`${backendUrl}/api/customer-master/dealer-names`);
            setDealerNames(response.data);
        } catch (error) {
            console.error("Gagal mengambil data Dealer Names:", error);
            notification.error({ message: "Gagal mengambil data Dealer Names dari server." });
        }
    }, []);

    const handleSameAsIdAddressChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;
            setSameAsIdAddressChecked(checked);

            if (checked) {
                const idAddressValues = form.getFieldsValue([
                    "address_line1",
                    "address_line2",
                    "rt_rw",
                    "provinsi",
                    "kabupaten",
                    "kecamatan",
                    "kelurahan",
                    "kode_pos",
                    "no_hp1",
                ]);

                form.setFieldsValue({
                    home_address_line1: idAddressValues.address_line1,
                    home_address_line2: idAddressValues.address_line2,
                    home_rtrw: idAddressValues.rt_rw,
                    homeprovinsi: idAddressValues.provinsi,
                    homekabupaten: idAddressValues.kabupaten,
                    homekecamatan: idAddressValues.kecamatan,
                    homekelurahan: idAddressValues.kelurahan,
                    homekodepos: idAddressValues.kode_pos,
                    homehp: idAddressValues.no_hp1,
                });


                if (idAddressValues.provinsi) {
                    await fetchKabupatens(idAddressValues.provinsi);
                }
                if (idAddressValues.kabupaten) {
                    await fetchKecamatans(idAddressValues.kabupaten);
                }
                if (idAddressValues.kecamatan) {
                    await fetchKelurahans(idAddressValues.kecamatan);
                }
                triggerAutosave(form.getFieldsValue());
            } else {

                form.setFieldsValue({
                    home_address_line1: undefined,
                    home_address_line2: undefined,
                    home_rtrw: undefined,
                    homeprovinsi: undefined,
                    homekabupaten: undefined,
                    homekecamatan: undefined,
                    homekelurahan: undefined,
                    homekodepos: undefined,
                    homehp: undefined,
                });
                setKabupatens([]);
                setKecamatans([]);
                setKelurahans([]);
                triggerAutosave(form.getFieldsValue());
            }
        },
        [form, fetchKabupatens, fetchKecamatans, fetchKelurahans, triggerAutosave],
    );

    useEffect(() => {
        return () => {
            if (autosaveTimeoutRef.current) {
                clearTimeout(autosaveTimeoutRef.current);
            }
            if (requiredFieldsTimeoutRef.current) {
                clearTimeout(requiredFieldsTimeoutRef.current);
            }
            notification.destroy('requiredFieldsNotification');
            notification.destroy('similarNamesNotification')
        };
    }, []);

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            const storedData = sessionStorage.getItem('selectedCustomerData');
            if (storedData) {
                try {
                    const parsedData = JSON.parse(storedData);
                    setPrefilledCustomerData(parsedData);
                    sessionStorage.removeItem('selectedCustomerData');
                } catch (error) {
                    console.error("Failed to parse stored customer data:", error);
                }
            }
        }
        fetchCustomerTypes();
        fetchIdTypes();
        fetchProvinces();
        fetchTermOfPayments();
        fetchTaxInvoiceTypes();
        fetchVatTransactionTypes();
        fetchBusinessTypes();
        fetchCustomerBehaviours();
        fetchJobTitles();
        fetchDealerNames();
        axios.get(`${backendUrl}/api/customer-master/all-data`).then(res => setExistingCustomers(res.data));
    }, [
        fetchCustomerTypes,
        fetchIdTypes,
        fetchProvinces,
        fetchTermOfPayments,
        fetchTaxInvoiceTypes,
        fetchVatTransactionTypes,
        fetchBusinessTypes,
        fetchCustomerBehaviours,
        fetchJobTitles,
        fetchDealerNames
    ]);

    useEffect(() => {

        if (prefilledCustomerData && customerTypes.length > 0 && dealerNames.length > 0 && provinces.length > 0) {
            const data = prefilledCustomerData;
            const formValues: any = {};
            formValues.cs_code = data.cs_code;
            formValues.record_status = data.record_status === "1" ? "Active" : (data.record_status === "2" ? "Deactive" : data.record_status);
            formValues.customer_type = data.customer_type;
            formValues.id_type = data.id_type;
            formValues.id_no = data.id_no;
            formValues.title_p = data.title_p;
            formValues.cs_name = data.cs_name;
            formValues.title_s = data.title_s;
            formValues.search_kw = data.search_kw;
            formValues.address_line1 = data.address_line1;
            formValues.address_line2 = data.address_line2;
            formValues.rt_rw = data.rt_rw;
            formValues.provinsi = data.provinsi;
            formValues.kabupaten = data.kabupaten;
            formValues.kecamatan = data.kecamatan;
            formValues.kelurahan = data.kelurahan;
            formValues.kode_pos = data.kode_pos;
            formValues.email = data.email;
            formValues.no_hp1 = data.no_hp1;
            formValues.no_hp2 = data.no_hp2;
            formValues.termofpayment = data.termofpayment;
            formValues.taxinvoicetype = data.taxinvoicetype;
            formValues.home_address_line1 = data.home_address_line1;
            formValues.home_address_line2 = data.home_address_line2;
            formValues.home_rtrw = data.home_rtrw;
            formValues.homeprovinsi = data.homeprovinsi;
            formValues.homekabupaten = data.homekabupaten;
            formValues.homekecamatan = data.homekecamatan;
            formValues.homekelurahan = data.homekelurahan;
            formValues.homekodepos = data.homekodepos;
            formValues.home_phone = data.home_phone;
            formValues.homefax_number = data.homefax_number;
            formValues.homehp = data.homehp;
            formValues.office_address_line1 = data.office_address_line1;
            formValues.office_address_line2 = data.office_address_line2;
            formValues.office_rtrw = data.office_rtrw;
            formValues.officeprovinsi = data.officeprovinsi;
            formValues.officekabupaten = data.officekabupaten;
            formValues.officekecamatan = data.officekecamatan;
            formValues.officekelurahan = data.officekelurahan;
            formValues.officekodepos = data.officekodepos;
            formValues.officetelpno = data.officetelpno;
            formValues.officefaxno = data.officefaxno;
            formValues.officephoneno = data.officephoneno;
            formValues.vatnpwp = data.vatnpwp;
            formValues.vatnpwpdate = data.vatnpwpdate ? moment(data.vatnpwpdate) : undefined;
            formValues.vatpkpdate = data.vatpkpdate ? moment(data.vatpkpdate) : undefined;
            formValues.vattransactioncode = data.vattransactioncode;
            formValues.vatname = data.vatname;
            formValues.vat_address1 = data.vat_address1;
            formValues.vat_address2 = data.vat_address2;
            formValues.vat_rtrw = data.vat_rtrw;
            formValues.vatprovinsi = data.vatprovinsi;
            formValues.vatkabupaten = data.vatkabupaten;
            formValues.vatkecamatan = data.vatkecamatan;
            formValues.vatkelurahan = data.vatkelurahan;
            formValues.vatkodepos = data.vatkodepos;
            formValues.vatkppcode = data.vatkppcode;
            formValues.vatkppdeskripsi = data.vatkppdeskripsi;
            formValues.businesstype = data.businesstype;
            formValues.companygroup = data.companygroup;
            formValues.website = data.website;
            formValues.customerclass = data.customerclass;
            formValues.customerbehaviour = data.customerbehaviour;
            formValues.gender = data.gender;
            formValues.birthday = data.birthday ? moment(data.birthday) : undefined;
            formValues.religion = data.religion;
            formValues.maritalstatus = data.maritalstatus;
            formValues.marriagedate = data.marriagedate ? moment(data.marriagedate) : undefined;
            formValues.jobtitle = data.jobtitle;
            formValues.hobby = data.hobby;
            formValues.note = data.note;
            formValues.correspondence_equal_to = data.correspondence_equal_to;
            formValues.billing_equal_to = data.billing_equal_to;
            formValues.credit_limit = data.credit_limit;
            formValues.remaining_credit_limit = data.remaining_credit_limit;
            formValues.dealername = data.dealername;
            formValues.collect = data.collect;
            formValues.dealersalesrep = data.dealersalesrep;
            formValues.contactdate = data.contactdate ? moment(data.contactdate) : undefined;
            form.setFieldsValue(formValues);

            if (data.provinsi) {
                fetchKabupatens(data.provinsi);
            }
            if (data.kabupaten) {
                fetchKecamatans(data.kabupaten);
            }
            if (data.kecamatan) {
                fetchKelurahans(data.kecamatan);
            }

            if (data.homeprovinsi) {
                fetchKabupatens(data.homeprovinsi);
            }
            if (data.homekabupaten) {
                fetchKecamatans(data.homekabupaten);
            }
            if (data.homekecamatan) {
                fetchKelurahans(data.homekecamatan);
            }

            if (data.officeprovinsi) {
                fetchKabupatens(data.officeprovinsi);
            }
            if (data.officekabupaten) {
                fetchKecamatans(data.officekabupaten);
            }
            if (data.officekecamatan) {
                fetchKelurahans(data.officekecamatan);
            }

            if (data.vatprovinsi) {
                fetchKabupatens(data.vatprovinsi);
            }
            if (data.vatkabupaten) {
                fetchKecamatans(data.vatkabupaten);
            }
            if (data.vatkecamatan) {
                fetchKelurahans(data.vatkecamatan);
            }

            if (data.id_no || data.address_line1 || data.address_line2) {
                setShowIdSection(true);
            } else {
                setShowIdSection(false);
            }

            setSameAsIdAddressChecked(false);
            const loadedRecordStatus = data.record_status;
            setIsActiveButtonDisabled(loadedRecordStatus === '1');
            setIsDeactivateButtonDisabled(loadedRecordStatus === '2');
            setIsCustomerTypeDisabled(loadedRecordStatus === '2');

            if (loadedRecordStatus === '2') {
                setAutosaveStatus('stopped');
            } else {
                setAutosaveStatus('idle');
            }

            triggerAutosave(form.getFieldsValue());
            setPrefilledCustomerData(null);
        }
    }, [prefilledCustomerData, customerTypes, dealerNames, provinces, form, triggerAutosave, fetchKabupatens, fetchKecamatans, fetchKelurahans, setShowIdSection]);

    return {
        form,
        generateNewCustomerCode,
        handleNew,
        customerTypes,
        showIdSection,
        setShowIdSection,
        idTypes,
        provinces,
        kabupatens,
        fetchKabupatens,
        kecamatans,
        fetchKecamatans,
        kelurahans,
        fetchKelurahans,
        sameAsIdAddressChecked,
        handleSameAsIdAddressChange,
        termOfPayments,
        taxInvoiceTypes,
        vatTransactionTypes,
        businessTypes,
        customerBehaviours,
        jobTitles,
        dealerNames,
        autosaveStatus,
        triggerAutosave,
        setAutosaveStatus,
        formatRupiahInput,
        parseRupiahInput,
        isKppModalVisible,
        showKppModal,
        handleKppModalOk,
        handleKppModalCancel,
        onSelectKpp,
        isActiveButtonDisabled,
        isDeactivateButtonDisabled,
        isCustomerTypeDisabled,
        handleDeactivateCustomer,
        handleActivateCustomer,
        similarNames,
        handleNameChange
    };
};

export const withCustomerMasterLogic = (WrappedComponent: React.ComponentType<any>) => {
    return (props: any) => {
        const {
            form,
            generateNewCustomerCode,
            handleNew,
            customerTypes,
            showIdSection,
            setShowIdSection,
            idTypes,
            provinces,
            kabupatens,
            fetchKabupatens,
            kecamatans,
            fetchKecamatans,
            kelurahans,
            fetchKelurahans,
            sameAsIdAddressChecked,
            handleSameAsIdAddressChange,
            termOfPayments,
            taxInvoiceTypes,
            vatTransactionTypes,
            businessTypes,
            customerBehaviours,
            jobTitles,
            dealerNames,
            autosaveStatus,
            triggerAutosave,
            setAutosaveStatus,
            formatRupiahInput,
            parseRupiahInput,
            isKppModalVisible,
            showKppModal,
            handleKppModalOk,
            handleKppModalCancel,
            onSelectKpp,
            isActiveButtonDisabled,
            isDeactivateButtonDisabled,
            isCustomerTypeDisabled,
            handleDeactivateCustomer,
            handleActivateCustomer,
            similarNames,
            handleNameChange
        } = useCustomerMasterLogic();
        return (
            <WrappedComponent
                {...props}
                form={form}
                generateNewCustomerCode={generateNewCustomerCode}
                handleNew={handleNew}
                customerTypes={customerTypes}
                showIdSection={showIdSection}
                setShowIdSection={setShowIdSection}
                idTypes={idTypes}
                provinces={provinces}
                kabupatens={kabupatens}
                fetchKabupatens={fetchKabupatens}
                kecamatans={kecamatans}
                fetchKecamatans={fetchKecamatans}
                kelurahans={kelurahans}
                fetchKelurahans={fetchKelurahans}
                sameAsIdAddressChecked={sameAsIdAddressChecked}
                handleSameAsIdAddressChange={handleSameAsIdAddressChange}
                termOfPayments={termOfPayments}
                taxInvoiceTypes={taxInvoiceTypes}
                vatTransactionTypes={vatTransactionTypes}
                businessTypes={businessTypes}
                customerBehaviours={customerBehaviours}
                jobTitles={jobTitles}
                dealerNames={dealerNames}
                autosaveStatus={autosaveStatus}
                triggerAutosave={triggerAutosave}
                setAutosaveStatus={setAutosaveStatus}
                formatRupiahInput={formatRupiahInput}
                parseRupiahInput={parseRupiahInput}
                isKppModalVisible={isKppModalVisible}
                showKppModal={showKppModal}
                handleKppModalOk={handleKppModalOk}
                handleKppModalCancel={handleKppModalCancel}
                onSelectKpp={onSelectKpp}
                isActiveButtonDisabled={isActiveButtonDisabled}
                isDeactivateButtonDisabled={isDeactivateButtonDisabled}
                isCustomerTypeDisabled={isCustomerTypeDisabled}
                handleDeactivateCustomer={handleDeactivateCustomer}
                handleActivateCustomer={handleActivateCustomer}
                similarNames={similarNames}
                handleNameChange={handleNameChange}
            />
        );
    };
};

export const initialRecordStatus = "Active";